<template>
  <div id="quickConfig">
    <div class="container">

      <Tabs v-if="ready">
        <TabSection v-for="option of allowedOptions" :key="`option-${option.id}`" :tag="option.tag"
          :title="$t('quick_config.select.' + option.id)" :subTitle="$t('quick_config.' + option.id + '_description')">

          <QuickCompanySetting v-if="option.component === 'QuickCompanySetting'" :fields="option.fields"
            :branch-id="branchId" :company-id="companyId" />

          <!-- Emergency closure -->
          <div class="emergencyClosure" v-else-if="option.component === 'EmergencyClosure'">
            <Panel :title="$t('settings.online_ordering')" collapsible>
              <div class="group">
                <input id="chkEmergencyClosure" type="checkbox" v-model="company.emergencyCloseManually" />
                <label for="chkEmergencyClosure">{{ $t("active") }}</label>
              </div>
              <div class="group">
                <LocalizedInput :title="$t('settings.emergency_close_message')"
                  :translate="company.emergencyCloseManuallyMessage" :textarea="true"></LocalizedInput>
              </div>
            </Panel>
            <Panel :title="$t('services.kiosk')" collapsible v-if="company.kiosk.activated">
              <div class="group">
                <input id="chkKioskClosure" type="checkbox" v-model="company.kiosk.emergency.emergencyCloseKioskManually" />
                <label for="chkKioskClosure">{{ $t("active") }}</label>
              </div>
              <div class="group">
                <LocalizedInput :title="$t('settings.emergency_close_message')"
                  :translate="company.kiosk.emergency.emergencyCloseKioskManuallyMessage" :textarea="true"></LocalizedInput>
              </div>
            </Panel>
          </div>

          <!-- Preparation delays -->
          <div class="preparationDelays" v-else-if="option.component === 'PreparationDelays'">
            <div v-for="method in activatedMethods" :key="`preparationDelays-${method}`" class="preparation"
              v-if="company.onlineOrdering[method].activated">
              <div class="icon">
                <svgicon :src="`icon/${method}.svg`" :width="16" :height="16"></svgicon>
              </div>
              <div class="title">{{ $t("quick_config." + method) }}</div>
              <div class="input">
                <input type="number" v-model="company.onlineOrdering[method].general.averageDelay" />
              </div>
            </div>
          </div>

          <!-- Opening hours -->
          <div class="openingHours" v-else-if="option.component === 'OpeningHours'">
            <Panel v-for="method in activatedMethods" :key="`openingHours-${method}`"
              :title="$t('quick_config.' + method)" collapsible collapsed>
              <div class="columns">
                <div class="column">
                  <DayTimeRange dayName="monday" :day="company.onlineOrdering[method].hours.monday"></DayTimeRange>
                  <DayTimeRange dayName="tuesday" :day="company.onlineOrdering[method].hours.tuesday"></DayTimeRange>
                  <DayTimeRange dayName="wednesday" :day="company.onlineOrdering[method].hours.wednesday"></DayTimeRange>
                  <DayTimeRange dayName="thursday" :day="company.onlineOrdering[method].hours.thursday"></DayTimeRange>
                </div>
                <div class="column">
                  <DayTimeRange dayName="friday" :day="company.onlineOrdering[method].hours.friday"></DayTimeRange>
                  <DayTimeRange dayName="saturday" :day="company.onlineOrdering[method].hours.saturday"></DayTimeRange>
                  <DayTimeRange dayName="sunday" :day="company.onlineOrdering[method].hours.sunday"></DayTimeRange>
                </div>
              </div>
            </Panel>
          </div>

          <ItemSnoozes ref="itemSnoozes" v-else-if="option.component === 'ItemSnoozes'" :company="companyId"
            :inventory="inventoryId" />

          <!--<StockEdit ref="stockEdit" v-else-if="option.component === 'StockEdit'" />-->
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="ready">
        <div class="btn btn-primary" @click="save">{{ $t("save") }}</div>
      </div>

    </div>
  </div>
</template>

<script>
  import Panel from "@/components/tools/Panel.vue";
  import ItemSnoozes from "@/views/settings/quick/ItemSnoozes";
  import QuickCompanySetting from "@/views/settings/quick/QuickCompanySetting";
  import StockEdit from "./StockEdit";
  import Tabs from "@/components/navigation/Tabs";
  import TabSection from "@/components/navigation/TabSection";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import { mapActions } from "vuex";
  import DayTimeRange from "@/components/tools/DayTimeRange";

  export default {
    name: "QuickConfig",

    components: { StockEdit, Panel, ItemSnoozes, QuickCompanySetting, Tabs, TabSection, LocalizedInput, DayTimeRange },

    data() {
      return {
        company: null,
        branchId: null,
        companyId: null,
        inventoryId: null,
        selected: { id: null },
        options: [
          {
            id: 'emergency_closure',
            component: 'EmergencyClosure'
          },
          // {
          //   id: 'by_pass_pos',
          //   component: 'QuickCompanySetting',
          //   fields: ['bypassPosMode', 'bypassPosOptions.emails']
          // },
          {
            id: 'preparation_delays',
            component: 'PreparationDelays'
          },
          {
            id: 'opening_hours',
            component: 'OpeningHours',
            fields: [
              'onlineOrdering.takeout.hours', 'onlineOrdering.delivery.hours',
              'onlineOrdering.inPlace.hours', 'onlineOrdering.hotel.hours',
              'onlineOrdering.catering.hours'
            ],
          },
          {
            id: 'snooze_item',
            component: 'ItemSnoozes',
          }
        ],
        ready: false
      }
    },

    computed: {
      user() {
        return this.$store.state.loggedUser
      },
      isSuperAdmin() {
        return this.user.isSuperAdmin;
      },
      allowedOptions() {
        let options = this.options;
        /*if (this.isSuperAdmin) {
          options.push({
                id: 'stock_edit',
                component: 'StockEdit',
                tag: 'Superadmin'
              })
        }*/
        if (this.user && this.user.permission && this.user.permission.list) {
          return options.filter(o => this.user.permission.blacklisted
            ? !this.user.permission.list.includes(o.id)
            : this.user.permission.list.includes(o.id)
          )
        } else {
          return options
        }
      },
      activatedMethods() {
        return Object.keys(this.company.onlineOrdering)
          .filter(k => this.company.onlineOrdering[k].activated);
      }
    },

    methods: {
      ...mapActions(['getCompany', 'saveCompany']),

      async save() {
        showSpinner();
        if (this.$refs.itemSnoozes) {
          await this.$refs.itemSnoozes[0].save();
        }
        if (this.isSuperAdmin && this.$refs.stockEdit) {
          await this.$refs.stockEdit[0].save();
        }
        let response = await this.saveCompany({
          branchId: this.branchId,
          companyId: this.companyId,
          data: this.company
        });
        hideSpinner();
        if (response) {
          toast(this.$t('quick_company.save_success'), 'success')
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      }
    },
    async mounted() {
      this.$store.state.pageTitleTranslationKey = "settings.quick_config";
      this.$store.state.pageIcon = "";

      let inventory = await this.$store.getters.getContextInventory;
      this.branchId = this.$route.query.branch;
      this.companyId = this.$route.query.company;
      this.inventoryId = inventory.id;
      this.company = await this.getCompany({
        branchId: this.branchId,
        companyId: this.companyId,
        fallback: true
      });
      this.ready = true;
    }
  }
</script>

<style lang="scss" scoped>
  #quickConfig {
    display: flex;
  }

  .panel .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .preparationDelays {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    grid-gap: 12px 32px;

    .preparation {
      display: flex;
      align-items: center;
      background-color: white;
      box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.1);
      padding: 12px;
      border-radius: 8px;

      .icon {
        width: 32px;
        height: 32px;
        background-color: #F5F5F5;
        border-radius: 6px;
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        fill: #ccc;
        flex-shrink: 0;
      }

      .title {
        flex-grow: 1;
        font-weight: bold;
        margin-right: 8px;
      }

      .input {
        position: relative;

        &:after {
          content: "minutes";
          position: absolute;
          right: 40px;
          top: 50%;
          transform: translateY(-50%);
        }

        input {
          width: 180px;
        }
      }
    }
  }

  .openingHours {
    .columns {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      grid-gap: 20px;

      .column {
        margin: 0;
      }
    }
  }
</style>

<style lang="scss">
  #quickConfig #Tabs {
    max-height: 100%;
    overflow: hidden;
    padding: 5px;
    margin: -5px;

    .page-content {
      overflow-y: auto;
    }

    .content {
      max-height: 100%;
    }

    .emergencyClosure .panel,
    .openingHours .panel {
      padding-top: 0;
    }
  }
</style>